import { useState, useRef, useCallback, useEffect } from 'react';
import toast from 'react-hot-toast';
import { creditsApi } from '@/lib/api/credits';
import { claudeAgentApi } from '@/lib/api/claude-agent';
import { byocApi } from '@/lib/api/byoc';
import type { CreditAlertLevel } from '@/components/agent/CreditsDisplay';

export interface UseCreditsVerificationOptions {
  user: { email?: string } | null;
  getIdToken: () => Promise<string | null>;
  workspaceId?: string; // Re-fetch when workspace changes (BYOC is workspace-specific)
}

export interface UseCreditsVerificationReturn {
  // Credits state
  creditBalance: number | null;
  allocatedCredits: number | null;
  isLoadingCredits: boolean;
  shownCreditAlerts: Set<CreditAlertLevel>;
  handleCreditAlertShown: (level: CreditAlertLevel) => void;
  fetchCredits: () => Promise<void>;
  resetShownCreditAlerts: () => void;

  // BYOC state (bypasses credit checks when connected)
  byocConnected: boolean;

  // Email verification state
  isEmailVerificationBlocked: boolean;
  setIsEmailVerificationBlocked: React.Dispatch<React.SetStateAction<boolean>>;
  isCheckingEmailVerification: boolean;
  isResendingVerificationEmail: boolean;
  isEmailVerifiedRef: React.MutableRefObject<boolean | null>;
  handleCheckEmailVerification: () => Promise<void>;
  handleResendVerificationEmail: () => Promise<void>;
}

/**
 * Hook for handling credits and email verification
 * Manages credit balance, alerts, and email verification flow
 */
export function useCreditsVerification({
  user,
  getIdToken,
  workspaceId,
}: UseCreditsVerificationOptions): UseCreditsVerificationReturn {
  // Credits state
  const [creditBalance, setCreditBalance] = useState<number | null>(null);
  const [allocatedCredits, setAllocatedCredits] = useState<number | null>(null);
  const [isLoadingCredits, setIsLoadingCredits] = useState(false);
  const [shownCreditAlerts, setShownCreditAlerts] = useState<Set<CreditAlertLevel>>(new Set());

  // BYOC state (bypasses credit checks when connected)
  const [byocConnected, setByocConnected] = useState(false);

  // Email verification state
  const [isEmailVerificationBlocked, setIsEmailVerificationBlocked] = useState(false);
  const [isCheckingEmailVerification, setIsCheckingEmailVerification] = useState(false);
  const [isResendingVerificationEmail, setIsResendingVerificationEmail] = useState(false);
  const isEmailVerifiedRef = useRef<boolean | null>(null);

  // Track which credit alerts have been shown (80%, 90% show once, insufficient always shows)
  const handleCreditAlertShown = useCallback((level: CreditAlertLevel) => {
    if (level !== 'insufficient') {
      setShownCreditAlerts(prev => new Set([...prev, level]));
    }
  }, []);

  // Reset shown credit alerts (called when switching sessions)
  const resetShownCreditAlerts = useCallback(() => {
    setShownCreditAlerts(new Set());
  }, []);

  // Fetch credits and BYOC status from API
  const fetchCredits = useCallback(async () => {
    if (!user) return;

    try {
      setIsLoadingCredits(true);
      const token = await getIdToken();
      if (!token) {
        return;
      }

      // Fetch credits and BYOC status in parallel
      const [details, byocStatus] = await Promise.all([
        creditsApi.getDetails(token),
        byocApi.getStatus(token).catch(() => ({ connected: false })), // Fail gracefully
      ]);

      const newBalance = typeof details.balance === 'string' ? parseFloat(details.balance) : details.balance;
      const newAllocated = typeof details.allocated === 'string' ? parseFloat(details.allocated) : details.allocated;

      setCreditBalance(newBalance);
      setAllocatedCredits(newAllocated);
      setByocConnected(byocStatus.connected);
    } catch (error) {
      console.error('[useCreditsVerification] Failed to fetch balance:', error);
    } finally {
      setIsLoadingCredits(false);
    }
  }, [user, getIdToken]);

  // Check email verification status when user clicks "I've Verified"
  const handleCheckEmailVerification = useCallback(async () => {
    setIsCheckingEmailVerification(true);
    try {
      const token = await getIdToken();
      if (!token) {
        toast.error('Please sign in again');
        return;
      }

      const status = await claudeAgentApi.checkEmailVerificationStatus(token);

      if (!status.requiresVerification) {
        isEmailVerifiedRef.current = true;
        setIsEmailVerificationBlocked(false);
        toast.success('Email verified! You can now continue chatting.');
      } else {
        toast.error('Email not yet verified. Please check your inbox and click the verification link.');
      }
    } catch (error) {
      console.error('[useCreditsVerification] Failed to check email verification:', error);
      toast.error('Failed to check verification status. Please try again.');
    } finally {
      setIsCheckingEmailVerification(false);
    }
  }, [getIdToken]);

  // Resend verification email via optiprime auth-api
  const handleResendVerificationEmail = useCallback(async () => {
    setIsResendingVerificationEmail(true);
    try {
      const idToken = await getIdToken();
      if (!idToken) {
        toast.error('Please sign in again');
        return;
      }

      const AUTH_API_URL = import.meta.env.VITE_AUTH_API_URL || 'http://localhost:9101';
      const response = await fetch(`${AUTH_API_URL}/api/auth/email/verify-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${idToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to send verification email');
      }

      toast.success('Verification email sent! Please check your inbox.');
    } catch (error) {
      console.error('[useCreditsVerification] Failed to resend verification email:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to send verification email');
    } finally {
      setIsResendingVerificationEmail(false);
    }
  }, [getIdToken]);

  // Check email verification on mount function
  const checkEmailVerificationOnMount = useCallback(async () => {
    if (!user) return;

    try {
      const token = await getIdToken();
      if (!token) return;

      const status = await claudeAgentApi.checkEmailVerificationStatus(token);
      isEmailVerifiedRef.current = status.emailVerified;

      if (status.requiresVerification) {
        console.log('[useCreditsVerification] User requires verification, blocking chat proactively');
        setIsEmailVerificationBlocked(true);
      }
    } catch (error) {
      console.error('[useCreditsVerification] Failed to check status on mount:', error);
      // Don't block on error - fail open
    }
  }, [user, getIdToken]);

  // Fetch credits on mount and when workspace changes
  // BYOC status is workspace-specific, so re-fetch when switching workspaces
  useEffect(() => {
    if (user) {
      fetchCredits();
    }
  }, [user, workspaceId, fetchCredits]);

  // Check email verification on mount
  useEffect(() => {
    if (user) {
      checkEmailVerificationOnMount();
    }
  }, [user, checkEmailVerificationOnMount]);

  return {
    // Credits
    creditBalance,
    allocatedCredits,
    isLoadingCredits,
    shownCreditAlerts,
    handleCreditAlertShown,
    fetchCredits,
    resetShownCreditAlerts,

    // BYOC (bypasses credit checks)
    byocConnected,

    // Email verification
    isEmailVerificationBlocked,
    setIsEmailVerificationBlocked,
    isCheckingEmailVerification,
    isResendingVerificationEmail,
    isEmailVerifiedRef,
    handleCheckEmailVerification,
    handleResendVerificationEmail,
  };
}
